/**
 * Shared decision form used by both create and edit views.
 *
 * Uses Refine's react-hook-form `useForm` with a Zod resolver bound to the
 * shared `createDecisionSchema`. Empty optional fields are stripped before
 * submission so optional columns stay null/absent.
 */
import { useForm } from "@refinedev/react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigation, type BaseRecord, type HttpError } from "@refinedev/core";
import {
  createDecisionSchema,
  type CreateDecisionInput,
  DECISION_STATUS,
  CONFIDENTIALITY,
} from "@aos/shared";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  SelectField,
  TextAreaField,
  TextField,
} from "../_shared/FormFields";

type FormValues = CreateDecisionInput;

/** Drop empty-string and undefined fields so optional columns stay absent. */
function clean(values: FormValues): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value === "" || value === undefined) continue;
    out[key] = value;
  }
  return out;
}

export function DecisionForm({ action }: { action: "create" | "edit" }) {
  const { list } = useNavigation();
  const {
    refineCore: { onFinish, formLoading },
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BaseRecord, HttpError, FormValues>({
    resolver: zodResolver(createDecisionSchema),
    refineCoreProps: { resource: "decisions", action, redirect: "show" },
  });

  const submit = handleSubmit((values) => {
    void onFinish(clean(values) as FormValues);
  });

  const isCreate = action === "create";

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title={isCreate ? "New decision" : "Edit decision"}
        subtitle={
          isCreate
            ? "Record a decision request and the rationale behind it."
            : "Update this decision request and its rationale."
        }
        icon="decisions"
      />
      <form onSubmit={submit}>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Decision</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <TextField<FormValues>
              name="title"
              label="Title"
              required
              register={register}
              error={errors.title}
            />
            <TextAreaField<FormValues>
              name="description"
              label="Description"
              register={register}
              error={errors.description}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField<FormValues>
                name="decision_type"
                label="Decision type"
                register={register}
                error={errors.decision_type}
              />
              <SelectField<FormValues>
                name="status"
                label="Status"
                register={register}
                error={errors.status}
                options={DECISION_STATUS}
                includeEmpty={false}
              />
            </div>
            <SelectField<FormValues>
              name="confidentiality"
              label="Confidentiality"
              register={register}
              error={errors.confidentiality}
              options={CONFIDENTIALITY}
              includeEmpty={false}
            />
            <TextAreaField<FormValues>
              name="rationale"
              label="Rationale (markdown)"
              register={register}
              error={errors.rationale}
              rows={5}
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField<FormValues>
                name="artifact_id"
                label="Artifact id"
                register={register}
                error={errors.artifact_id}
                placeholder="art_…"
              />
              <TextField<FormValues>
                name="project_id"
                label="Project id"
                register={register}
                error={errors.project_id}
                placeholder="prj_…"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <TextField<FormValues>
                name="requested_by_user_id"
                label="Requested by (user id)"
                register={register}
                error={errors.requested_by_user_id}
              />
              <TextField<FormValues>
                name="decided_by_user_id"
                label="Decided by (user id)"
                register={register}
                error={errors.decided_by_user_id}
              />
            </div>
          </CardContent>
        </Card>
        <div className="mt-4 flex gap-2">
          <Button type="submit" disabled={formLoading}>
            {formLoading ? "Saving…" : "Save"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => list("decisions")}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}

export default DecisionForm;
